import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform, animate } from "framer-motion";
import { Zap, ShieldCheck, BadgeCheck, RefreshCw, Star, Trophy, UserCheck } from "lucide-react";
import { CTAButton } from "./CTAButton";
import { HERO_IMAGE, MASCOT_IMAGE, PRODUCT } from "../../config";

const badges = [
  { icon: Zap, label: "Aktivasi Cepat" },
  { icon: RefreshCw, label: "Otomatis" },
  { icon: BadgeCheck, label: "Legal" },
  { icon: ShieldCheck, label: "Garansi" },
  { icon: UserCheck, label: "Bisa Pakai Akun Pembeli" },
];

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] } }),
};

const PromoPrice = ({ start }) => {
  const mv = useMotionValue(PRODUCT.priceNormal);
  const [val, setVal] = useState(PRODUCT.priceNormal);
  useEffect(() => {
    if (!start) return;
    const controls = animate(mv, PRODUCT.pricePromo, {
      duration: 1.3,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [start, mv]);
  return <>Rp{val.toLocaleString("id-ID")}</>;
};

export const Hero = () => {
  const [priceStage, setPriceStage] = useState(0);
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 20 });

  useEffect(() => {
    const t1 = setTimeout(() => setPriceStage(1), 1400);
    const t2 = setTimeout(() => setPriceStage(2), 2300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const onMouseMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24 hero-bg" onMouseMove={onMouseMove} ref={ref}>
      <div className="absolute inset-0 hero-grid pointer-events-none" />
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-blue-400/20 blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-cyan-300/20 blur-[100px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-8 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200/70 px-4 py-2 mb-6"
            data-testid="hero-promo-badge"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-600" />
            </span>
            <span className="text-xs md:text-sm font-bold tracking-[0.14em] uppercase text-blue-700">Promo Khusus Bulan Ini</span>
          </motion.div>

          <div className="flex items-start gap-2 sm:gap-4">
            <h1 className="font-heading font-extrabold tracking-tight text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.03]">
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" variants={lineReveal} initial="hidden" animate="show" custom={0}>
                  Google
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span
                  className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500"
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                  custom={1}
                >
                  Gemini Pro
                </motion.span>
              </span>
            </h1>
            <div className="relative shrink-0 -mt-2 sm:-mt-1 h-24 sm:h-28 lg:h-32">
              <motion.div
                initial={{ opacity: 0, scale: 0.4, rotate: -12 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 180, damping: 14, delay: 0.5 }}
                className="mix-blend-multiply"
              >
                <img
                  src={MASCOT_IMAGE}
                  alt="Maskot AI"
                  width="1024"
                  height="1024"
                  decoding="async"
                  className="mascot-bob w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 pointer-events-none select-none"
                  data-testid="hero-mascot"
                />
              </motion.div>
            </div>
          </div>

          <div className="overflow-hidden mt-1">
            <motion.div
              variants={lineReveal}
              initial="hidden"
              animate="show"
              custom={2}
              className="flex items-center gap-3 sm:gap-4 flex-wrap"
            >
              <span
                className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 shimmer-text leading-none pb-1"
                data-testid="hero-duration-badge"
              >
                18 Bulan
              </span>
              <motion.span
                initial={{ opacity: 0, scale: 0, rotate: 12 }}
                animate={{ opacity: 1, scale: 1, rotate: -3 }}
                transition={{ type: "spring", stiffness: 260, damping: 13, delay: 0.9 }}
                className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs sm:text-sm font-extrabold px-3.5 py-1.5 uppercase tracking-wide shadow-[0_6px_20px_rgba(245,158,11,0.45)]"
                data-testid="hero-bestseller-badge"
              >
                <Trophy className="w-4 h-4" strokeWidth={2.5} />
                Terlaris
              </motion.span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="flex flex-wrap gap-2.5 mt-6"
          >
            {badges.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/80 backdrop-blur border border-slate-200 px-3.5 py-1.5 text-sm font-semibold text-slate-700 shadow-[0_2px_10px_rgba(0,0,0,0.04)]"
              >
                <Icon className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />
                {label}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-8 rounded-3xl bg-white/70 backdrop-blur-xl border border-white/60 shadow-[0_8px_40px_rgba(37,99,235,0.10)] p-6 md:p-7 max-w-lg"
            data-testid="hero-price-card"
          >
            <p className="text-xs font-bold tracking-[0.18em] uppercase text-slate-400 mb-2">Harga Normal</p>
            <div className="relative inline-block">
              <span
                className={`font-heading text-2xl md:text-3xl font-bold transition-all duration-700 ${
                  priceStage >= 2 ? "text-slate-400" : "text-slate-800 price-glow"
                }`}
                data-testid="hero-normal-price"
              >
                {PRODUCT.priceNormalLabel}
              </span>
              <motion.span
                initial={{ scaleX: 0 }}
                animate={{ scaleX: priceStage >= 1 ? 1 : 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute left-0 top-1/2 w-full h-[3px] bg-red-500 origin-left rounded-full"
              />
            </div>

            <div className="flex items-end gap-3 flex-wrap mt-3">
              <div>
                <p className="text-xs font-bold tracking-[0.18em] uppercase text-blue-600 mb-1">Harga Hari Ini</p>
                <motion.span
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={priceStage >= 2 ? { opacity: 1, scale: 1 } : {}}
                  transition={{ type: "spring", stiffness: 200, damping: 16 }}
                  className="font-heading text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 block"
                  data-testid="hero-promo-price"
                >
                  <PromoPrice start={priceStage >= 2} />
                </motion.span>
              </div>
              <motion.span
                initial={{ opacity: 0, scale: 0, rotate: -12 }}
                animate={priceStage >= 2 ? { opacity: 1, scale: 1, rotate: -6 } : {}}
                transition={{ type: "spring", stiffness: 300, damping: 14, delay: 0.5 }}
                className="inline-block bg-gradient-to-r from-red-600 to-red-500 text-white text-sm font-extrabold px-3.5 py-1.5 rounded-full shadow-[0_6px_20px_rgba(239,68,68,0.4)] mb-2"
                data-testid="hero-discount-badge"
              >
                HEMAT 98%
              </motion.span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 mt-8"
          >
            <CTAButton source="hero" size="lg" testId="hero-order-button" className="w-full sm:w-auto" />
            <button
              onClick={() => document.getElementById("fitur")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-features-button"
              className="inline-flex items-center justify-center rounded-full border-2 border-slate-300 bg-white/70 backdrop-blur px-8 py-4 font-bold text-slate-700 hover:border-blue-400 hover:text-blue-700 transition-colors"
            >
              Lihat Fitur
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="flex items-center gap-3 mt-8"
          >
            <div className="flex -space-x-2">
              {[12, 33, 47, 5].map((n) => (
                <img
                  key={n}
                  src={`https://i.pravatar.cc/64?img=${n}`}
                  alt="pengguna"
                  width="32"
                  height="32"
                  className="w-8 h-8 rounded-full border-2 border-white"
                  loading="lazy"
                  decoding="async"
                />
              ))}
            </div>
            <div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-xs text-slate-500 font-medium">Dipercaya 2.500+ pengguna di Indonesia</p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
          className="relative hidden lg:block"
        >
          <div className="relative float-slow">
            <img
              src={HERO_IMAGE}
              alt="Ilustrasi AI Premium Gemini"
              width="1024"
              height="1024"
              fetchPriority="high"
              decoding="async"
              className="w-full max-w-md mx-auto rounded-[32px] shadow-[0_30px_80px_rgba(37,99,235,0.25)]"
            />
            <div className="absolute -left-8 top-10 float-delayed glass-card px-4 py-3 rounded-2xl">
              <p className="font-heading font-extrabold text-blue-600 text-xl">5TB</p>
              <p className="text-xs font-semibold text-slate-600">Cloud Storage</p>
            </div>
            <div className="absolute -right-4 top-1/3 float-slow2 glass-card px-4 py-3 rounded-2xl">
              <p className="font-heading font-extrabold text-blue-600 text-xl">18 Bulan</p>
              <p className="text-xs font-semibold text-slate-600">Masa Aktif</p>
            </div>
            <div className="absolute left-2 -bottom-4 float-delayed2 glass-card px-4 py-3 rounded-2xl">
              <p className="font-heading font-extrabold text-blue-600 text-xl">Gemini 3.1</p>
              <p className="text-xs font-semibold text-slate-600">Pro Model</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
